/**
 * VerdictByPlacement — Recharts StackedBarChart showing verdict distribution per placement.
 * One bar per placement (Feed, Reels, Stories, etc.). Each bar segments: Scale/Fix/Kill.
 */

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

import { useVerdictSummary } from '@/hooks/useDashboard';
import { useDashboardContext } from '@/pages/dashboard/DashboardContext';

// ============================================================================
// CONSTANTS
// ============================================================================

const VERDICT_COLORS = {
  scale: '#22C55E',
  fix: '#F59E0B',
  kill: '#EF4444',
} as const;

// ============================================================================
// COMPONENT
// ============================================================================

export function VerdictByPlacement() {
  const { filters } = useDashboardContext();
  const { data } = useVerdictSummary(filters);

  const placements = data?.verdictByPlacement;
  if (!placements || placements.length === 0) return null;

  return (
    <div className="rounded-xl bg-surface shadow-sm border border-border-subtle p-3">
      <div className="text-xs font-semibold text-primary mb-2">Verdict by Placement</div>

      <ResponsiveContainer width="100%" height={160}>
        <BarChart data={placements} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
          <XAxis
            dataKey="placement"
            tick={{ fontSize: 10, fill: '#8B95A7' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 10, fill: '#8B95A7' }}
            axisLine={false}
            tickLine={false}
            width={24}
          />
          <Tooltip
            contentStyle={{
              fontSize: 11,
              borderRadius: 8,
              border: '1px solid rgb(var(--color-border-subtle))',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          />
          <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 10 }} />
          <Bar
            dataKey="scale"
            name="Scale"
            stackId="verdict"
            fill={VERDICT_COLORS.scale}
            radius={[0, 0, 0, 0]}
          />
          <Bar
            dataKey="fix"
            name="Fix"
            stackId="verdict"
            fill={VERDICT_COLORS.fix}
            radius={[0, 0, 0, 0]}
          />
          <Bar
            dataKey="kill"
            name="Kill"
            stackId="verdict"
            fill={VERDICT_COLORS.kill}
            radius={[2, 2, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
